import React, { useState, useCallback, useMemo, useRef } from 'react';
import clsx from 'clsx';

/**
 * A flexible and dynamic slider component.
 * @param {object} props - The component props.
 * @param {number} [props.min=0] - The minimum value of the slider.
 * @param {number} [props.max=100] - The maximum value of the slider.
 * @param {number} [props.step=1] - The step increment of the slider.
 * @param {number} props.value - The current value of the slider.
 * @param {function(number): void} props.onChange - Callback function when the value changes.
 * @param {string} [props.label] - An optional label to display above the slider.
 * @param {boolean} [props.showValue=false] - Whether to display the current value.
 * @param {'default' | 'inline'} [props.layout='default'] - The layout of the component.
 * @param {string} [props.className] - Tailwind classes for the main container.
 * @param {string} [props.trackClassName] - Tailwind classes for the slider track.
 * @param {string} [props.activeTrackClassName] - Tailwind classes for the active part of the track.
 * @param {string} [props.thumbClassName] - Tailwind classes for the slider thumb.
 * @param {string} [props.unit] - Optional unit to display next to the value (e.g., "mm", "%", "°").
 */
const DynamicSlider = ({
  min = 0,
  max = 100,
  step = 1,
  value,
  onChange,
  label,
  showValue = true,
  layout = 'default',
  className,
  trackClassName,
  activeTrackClassName,
  thumbClassName,
  thumbColor = '#ffb900',
  labelClassName,
  valueClassName,
  unit,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [tempValue, setTempValue] = useState('');
  const inputRef = useRef(null);

  // Calculate the percentage of the slider's progress
  const progress = useMemo(() => {
    const range = max - min;
    if (range === 0) return 0;
    return ((value - min) / range) * 100;
  }, [value, min, max]);

  const handleValueChange = useCallback((e) => {
    onChange(parseFloat(e.target.value));
  }, [onChange]);

  // FIX: Calculate width based on max value length AND step precision
  const { valueDisplayWidth, decimalPlaces } = useMemo(() => {
    const stepString = String(step);
    const places = stepString.includes('.') ? stepString.split('.')[1].length : 0;
    
    const formatValue = (num) => num.toFixed(places);
    const maxLengthString = formatValue(max);
    const minLengthString = formatValue(min);
    
    const finalLength = Math.max(maxLengthString.length, minLengthString.length);
    
    return {
      valueDisplayWidth: `${finalLength + 2}ch`,
      decimalPlaces: places,
    };
  }, [max, min, step]);

  // Dynamically create styles for the slider thumb to work across browsers
  // The color is sampled from the provided image: a vibrant purple.
  const rawId = React.useId();
const sliderId = React.useMemo(
  () => `dynamic-slider-${String(rawId).replace(/[^a-zA-Z0-9_-]/g, '_')}`,
  [rawId]
);

  const formatValue = (num) => num.toFixed(decimalPlaces);

  const handleValueClick = useCallback(() => {
    setIsEditing(true);
    setTempValue(formatValue(value));
    setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.focus();
        inputRef.current.select();
      }
    }, 0);
  }, [value, formatValue]);

  const handleInputChange = useCallback((e) => {
    setTempValue(e.target.value);
  }, []);

  const handleInputSubmit = useCallback(() => {
    const numValue = parseFloat(tempValue);
    if (!isNaN(numValue)) {
      const clampedValue = Math.max(min, Math.min(max, numValue));
      onChange(clampedValue);
    }
    setIsEditing(false);
    setTempValue('');
  }, [tempValue, min, max, onChange]);

  const handleInputKeyDown = useCallback((e) => {
    if (e.key === 'Enter') {
      handleInputSubmit();
    } else if (e.key === 'Escape') {
      setIsEditing(false);
      setTempValue('');
    }
  }, [handleInputSubmit]);

  const handleInputBlur = useCallback(() => {
    handleInputSubmit();
  }, [handleInputSubmit]);

  const sliderStyles = `
    #${sliderId}::-webkit-slider-thumb {
      -webkit-appearance: none;
      appearance: none;
      width: 1rem; /* 20px */
      height: 1rem; /* 20px */
      border-radius: 9999px;
      background-color: ${thumbColor};
      cursor: pointer;
      margin-top: 0px; /* Center thumb on the track */
      box-shadow: 0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1);
      transition: transform 0.2s ease-in-out;
    }
    #${sliderId}:hover::-webkit-slider-thumb {
      transform: scale(1.1);
    }
    #${sliderId}::-moz-range-thumb {
      width: 1rem;
      height: 1rem;
      border-radius: 9999px;
      background-color: ${thumbColor};
      cursor: pointer;
      border: none;
      box-shadow: 0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1);
      transition: transform 0.2s ease-in-out;
    }
    #${sliderId}:hover::-moz-range-thumb {
        transform: scale(1.1);
    }
  `;

  const sliderInput = (
    <div className="relative h-5 flex items-center flex-grow mt-[2px]">
      {/* Base Track */}
      <div className={clsx('absolute w-full h-2 bg-gray-200 rounded-full', trackClassName)} />
      {/* Active/Progress Track */}
      <div
        className={clsx('absolute h-2 bg-violet-400 rounded-full', activeTrackClassName)}
        style={{ width: `${progress}%` }}
      />
      {/* Actual Range Input (transparent) */}
      <input
        id={sliderId}
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={handleValueChange}
        className={clsx('absolute w-full h-2 appearance-none bg-transparent cursor-pointer', thumbClassName)}
        aria-label={label || 'Slider'}
      />
    </div>
  );

  return (
    <>
      <style>{sliderStyles}</style>
      {layout === 'inline' ? (
        <div className={clsx('w-full font-sans flex items-center', className)}>
          {label && <label className={clsx("text-sm text-[#6b7280] mr-4 whitespace-nowrap", labelClassName)}>{label}</label>}
          {sliderInput}
          {showValue && (
            <div className="flex items-center ml-4">
              {isEditing ? (
                <input
                  ref={inputRef}
                  type="text"
                  value={tempValue}
                  onChange={handleInputChange}
                  onKeyDown={handleInputKeyDown}
                  onBlur={handleInputBlur}
                  className={clsx("text-sm text-[#6b7280] bg-[#f9fafb] text-center px-2 py-1 rounded border-none outline-none", valueClassName)}
                  style={{ width: valueDisplayWidth }}
                />
              ) : (
                <span
                  className={clsx("text-sm text-[#6b7280] bg-[#f9fafb] text-center px-2 py-1 rounded cursor-pointer hover:bg-gray-100", valueClassName)}
                  style={{ width: valueDisplayWidth }}
                  onClick={handleValueClick}
                >
                  {formatValue(value)}
                </span>
              )}
              {unit && <span className="text-xs text-gray-400 ml-1">{unit}</span>}
            </div>
          )}
        </div>
      ) : (
        <div className={clsx('w-full font-sans', className)}>
          {(label || showValue) && (
            <div className="flex justify-between items-center mb-2">
              {label && <label className={clsx("text-sm text-[#6b7280]", labelClassName)}>{label}</label>}
              {showValue && (
                <div className="flex items-center">
                  {isEditing ? (
                    <input
                      ref={inputRef}
                      type="text"
                      value={tempValue}
                      onChange={handleInputChange}
                      onKeyDown={handleInputKeyDown}
                      onBlur={handleInputBlur}
                      className={clsx("text-sm text-[#6b7280] bg-[#f9fafb] px-2 py-1 rounded border-none outline-none text-center", valueClassName)}
                      style={{ width: valueDisplayWidth }}
                    />
                  ) : (
                    <span
                      className={clsx("text-sm text-[#6b7280] bg-[#f9fafb] px-2 py-1 rounded text-center cursor-pointer hover:bg-gray-100", valueClassName)}
                      style={{ width: valueDisplayWidth }}
                      onClick={handleValueClick}
                    >
                      {formatValue(value)}
                    </span>
                  )}
                  {unit && <span className="text-xs text-gray-400 ml-1">{unit}</span>}
                </div>
              )}
            </div>
          )}
          {sliderInput}
        </div>
      )}
    </>
  );
};

export default DynamicSlider;